<template>
    <div v-if="type === 'radio'" class="ui-radio-group" :class="[className, { error: shouldShowError }]">
        <div v-for="checkbox in checkboxes" :key="checkbox.id" :class="['ui-radio', checkboxesClass]">
            <label :for="checkbox.id" class="ui-radio__button">
                <input
                    type="checkbox"
                    v-model="checkbox.checked"
                    :id="checkbox.id"
                    :name="checkbox.name"
                    :value="checkbox.value"
                    @change="handleCheckboxChange(checkbox)"
                    @blur="blurCheck()"
                />
                <img
                    v-if="checkbox.imageChecked"
                    class="checkbox_image"
                    :src="
                        require('@/assets/IMGS/SVG/' + (checkbox.checked ? checkbox.imageChecked : checkbox.imageNot))
                    "
                    alt="dynamic icon"
                />
                <span v-else class="ui-radio__button-checkmark"></span>
            </label>
            <label class="ui-radio__button-label" :for="checkbox.id">{{ checkbox.label }}</label>
        </div>
        <span v-if="shouldShowError && errorMessage !== ''" class="error-message">{{ errorMessage }}</span>
    </div>

    <div v-else class="ui-checkbox-group" :class="[className, { error: shouldShowError }]">
        <label v-for="checkbox in checkboxes" :key="checkbox.id" class="ui-checkbox">
            <div class="ui-checkbox__button">
                <input
                    type="checkbox"
                    v-model="checkbox.checked"
                    :id="checkbox.id"
                    :name="checkbox.name"
                    :value="checkbox.value"
                    class="checkbox"
                    @change="handleCheckboxChange(checkbox)"
                    @blur="blurCheck()"
                />
                <img
                    v-if="checkbox.imageChecked"
                    class="checkbox_image"
                    :src="
                        require('@/assets/IMGS/SVG/' + (checkbox.checked ? checkbox.imageChecked : checkbox.imageNot))
                    "
                    alt="dynamic icon"
                />
                <img
                    v-else
                    class="checkbox_default_image"
                    :src="require('@/assets/IMGS/SVG/' + (checkbox.checked ? 'chbx_green.svg' : 'chbx_gray.svg'))"
                />
            </div>

            <div class="checkmark_text" :for="checkbox.id">{{ checkbox.label }}</div>
        </label>
        <span v-if="shouldShowError && errorMessage !== ''" class="error-message">{{ errorMessage }}</span>
    </div>
</template>

<script>
export default {
    props: {
        type: {
            type: String,
            default: 'radio',
        },
        name: {
            type: String,
        },
        checkboxes: {
            type: Array,
            required: true,
        },
        checkboxesClass: {
            type: String,
            default: 'checkbox',
        },
        className: {
            type: String,
        },
        maxSelected: {
            type: Number,
            default: Infinity,
        },
        requiredCount: {
            type: Number,
            default: 0,
        },
        maxPercent: {
            type: Number,
            default: 0,
        },
    },
    data() {
        return {
            errorsCheckbox: [],
            errorMessage: '',
            percentageAssigned: false,
            userInteraction: false,
        };
    },
    watch: {
        checkboxes: {
            handler() {
                this.validateCheckboxes();
            },
            deep: true,
        },
    },
    computed: {
        shouldShowError() {
            return this.userInteraction && this.errorsCheckbox[this.name].hasError;
        },
        hasCheckedCheckbox() {
            return this.checkboxes.some((checkbox) => checkbox.checked);
        },
        selectedCheckboxes() {
            if (this.hasCheckedCheckbox) {
                return this.checkboxes.filter((checkbox) => checkbox.checked);
            }
            return [];
        },
    },
    mounted() {
        this.validateCheckboxes();
    },
    methods: {
        blurCheck() {
            this.userInteraction = true;
            this.validateCheckboxes();
        },
        handleCheckboxChange(checkbox) {
            this.userInteraction = true;

            if (checkbox.checked) {
                this.checkboxes.forEach((cb) => {
                    if (cb !== checkbox && cb.name === checkbox.name) {
                        cb.checked = false;
                    }
                });
            } else {
                const isMandatoryChecked =
                    (this.selectedCheckboxes.includes(checkbox) && checkbox.required) || this.type == 'radio';

                if (isMandatoryChecked) {
                    checkbox.checked = true;
                }
            }

            this.$emit('change', checkbox);
            this.validateCheckboxes();
        },
        validateCheckboxes() {
            if (this.selectedCheckboxes.length < this.requiredCount) {
                this.errorsCheckbox[this.name] = { hasError: true };
                this.$emit('error', { hasError: true, name: this.name, percent: 0 });
            } else {
                this.errorMessage = '';
                const completionPercentage = this.calculateCompletionPercentage();
                this.$emit('completion', { percent: completionPercentage, name: this.name });
                this.errorsCheckbox[this.name] = { hasError: false };
                this.$emit('error', { hasError: false, name: this.name, percent: this.maxPercent });
            }
        },
        calculateCompletionPercentage() {
            const selectedCount = this.selectedCheckboxes.length;
            const totalCheckboxes = this.requiredCount;

            if (selectedCount === 0) {
                return 0;
            }

            return (selectedCount / totalCheckboxes) * 100;
        },
    },
};
</script>

<style scoped lang="scss">
.ui-radio-group,
.ui-checkbox-group {
    display: flex;
    flex-wrap: wrap;
    gap: 8px 25px;

    &--column {
      flex-direction: column;
      gap: 8px;
    }

    &--flat {
      gap: 0;
    }
}

// UI-RADIO
.ui-radio {
    display: inline-flex;
    align-items: center;
    gap: 15px;

    border-width: 1px;
    border-style: solid;
    border-color: transparent;
    border-radius: 5px;
    user-select: none;

    &__button {
        display: inline-flex;
        justify-content: center;
        align-items: center;
        position: relative;
        width: 24px;
        height: 24px;
        background: #fff;
        border-radius: 50%;
        cursor: pointer;

        input {
            position: absolute;
            opacity: 0;
            width: 100%;
            height: 100%;
        }

        &-checkmark {
            display: inline-flex;
            justify-content: center;
            align-items: center;
            width: 24px;
            height: 24px;
            border: 2px solid #6e7675;
            border-radius: 50%;

            &:after {
                content: '';
                display: none;
                width: 12px;
                height: 12px;
                border-radius: 50%;
                background: #0eccaa;
            }
        }

        &-label {
            cursor: pointer;
            position: relative;
            display: flex;
            justify-content: flex-start;
            align-items: flex-start;
            text-align: start;
            font-size: 18px;
            font-weight: 400;
            line-height: 15px;
            color: #6e7675;

            &:empty {
              display: none;
            }
        }
    }

    &--icon {
      .ui-radio__button {
        width: 64px;
        height: 64px;
      }
    }
}

.ui-radio__button:hover input ~ .checkmark_img {
    filter: invert(70%) sepia(7%) saturate(229%) hue-rotate(123deg) brightness(91%) contrast(83%);
}

.ui-radio__button input:checked ~ .checkmark_img {
    filter: invert(20%) sepia(33%) saturate(7380%) hue-rotate(120deg) brightness(94%) contrast(89%);
}

.ui-radio__button:hover input ~ .ui-radio__button-checkmark {
    border: 2px solid #0eccaa;
}

.ui-radio__button input:checked ~ .ui-radio__button-checkmark {
    border: 2px solid #0eccaa;
}

.ui-radio__button input:checked ~ .ui-radio__button-checkmark:after {
    display: block;
}

.checkmark_img {
    position: absolute;
    filter: invert(45%) sepia(6%) saturate(357%) hue-rotate(123deg) brightness(98%) contrast(87%);
}

// UI-CHECKBOX
.ui-checkbox {
    display: inline-flex;
    align-items: center;
    gap: 15px;

    border-width: 1px;
    border-style: solid;
    border-color: transparent;
    border-radius: 5px;
    user-select: none;

    &__button {
        display: flex;
        justify-content: center;
        align-items: center;
        height: 100%;
        position: relative;
        cursor: pointer;
        
        input {
            position: absolute;
            opacity: 0;
            width: 100%;
            height: 100%;
        }
    }

    // .label_checkbox {
    //     display: flex;
    //     width: fit-content;
    //     height: fit-content;
    // }

    /* Create a custom checkbox */
    // .container-checkbox .checkmark {
    //     position: absolute;
    //     top: 0;
    //     left: 0;
    //     height: 20px;
    //     width: 20px;
    //     border-radius: 5px;
    //     border: 1px solid var(--color-grey);
    // }

    .container-checkbox:hover input ~ .checkmark {
        background-color: var(--color-green);
    }

    /* When the checkbox is checked, add a blue background */
    .container-checkbox input:checked ~ .checkmark {
        background-color: var(--color-green);
        border-color: var(--color-green);
    }

    /* Create the checkmark/indicator (hidden when not checked) */
    .container-checkbox .checkmark:after {
        content: '';
        position: absolute;
        display: none;
    }

    /* Show the checkmark when checked */
    .container-checkbox input:checked ~ .checkmark:after {
        display: block;
    }

    /* Style the checkmark/indicator */
    .container-checkbox .checkmark:after {
        bottom: 4px;
        width: 5px;
        height: 10px;
        border: solid white;
        border-width: 0 3.2px 3px 0;
        -webkit-transform: rotate(45deg);
        -ms-transform: rotate(45deg);
        transform: rotate(45deg);
    }

    .checkmark_text {
        font-size: 14px;
    }
}

.error {
    width: fit-content;
    padding-top: 5px;
    padding-left: 5px;
    padding-right: 15px;
    border: 1px solid rgba(0, 0, 0, 0);
    border-radius: 5px;
    border-color: var(--color-red) !important;
    border-width: 1px;
}
.error-message {
    position: absolute;
    display: flex;
    margin-top: 60px;
    margin-left: -5px;
    color: var(--color-red);
}

.checkbox_image {
    min-width: 64px;
    min-height: 64px;
    max-width: 64px;
    max-height: 64px;
    margin: auto;
}

.checkbox_default_image {
    min-width: 24px;
    min-height: 24px;
    max-width: 24px;
    max-height: 24px;
    margin: auto;
}
</style>
